// ---------------------------------------------------------------------
//  @ Role Permissions Reducer
// ---------------------------------------------------------------------

import { createReducer, on } from '@ngrx/store';
import * as RolePermissionsActions from './role-permissions.actions';
import { RolePermissionAssignment } from './role-permissions.models';

// ----------------------------------------------------------------------
// @ State
// ----------------------------------------------------------------------

export interface RolePermissionsState {
    rolePermissions: RolePermissionAssignment[];
    isLoading: boolean;
    error: string | null;
}

// ----------------------------------------------------------------------
// @ Initial State
// ----------------------------------------------------------------------

export const initialRolePermissionsState: RolePermissionsState = {
    rolePermissions: [],
    isLoading: false,
    error: null,
};

// ----------------------------------------------------------------------
// @ Reducer
// ----------------------------------------------------------------------

export const rolePermissionsReducer = createReducer(
    initialRolePermissionsState,

    // On Load All
    on(RolePermissionsActions.loadRolePermissions, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        RolePermissionsActions.loadRolePermissionsSuccess,
        (state, { rolePermissions }) => ({
            ...state,
            isLoading: false,
            rolePermissions: rolePermissions,
        })
    ),
    on(
        RolePermissionsActions.loadRolePermissionsFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // On Load By Role
    on(RolePermissionsActions.loadRolePermissionsByRole, (state) => ({
        ...state,
        isLoading: true,
    })),

    // On Create
    on(RolePermissionsActions.createRolePermission, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        RolePermissionsActions.createRolePermissionSuccess,
        (state, { rolePermission }) => ({
            ...state,
            isLoading: false,
            rolePermissions: [...state.rolePermissions, rolePermission],
        })
    ),
    on(
        RolePermissionsActions.createRolePermissionFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // On Bulk Create
    on(RolePermissionsActions.bulkCreateRolePermissions, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        RolePermissionsActions.bulkCreateRolePermissionsSuccess,
        (state, { rolePermissions }) => ({
            ...state,
            isLoading: false,
            rolePermissions: [...state.rolePermissions, ...rolePermissions],
        })
    ),
    on(
        RolePermissionsActions.bulkCreateRolePermissionsFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // On Update
    on(RolePermissionsActions.updateRolePermission, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        RolePermissionsActions.updateRolePermissionSuccess,
        (state, { rolePermission }) => ({
            ...state,
            isLoading: false,
            rolePermissions: state.rolePermissions.map((rp) =>
                rp.id === rolePermission.id ? rolePermission : rp
            ),
        })
    ),
    on(
        RolePermissionsActions.updateRolePermissionFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // On Delete
    on(RolePermissionsActions.deleteRolePermission, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(RolePermissionsActions.deleteRolePermissionSuccess, (state, { id }) => ({
        ...state,
        isLoading: false,
        rolePermissions: state.rolePermissions.filter((rp) => rp.id !== id),
    })),
    on(
        RolePermissionsActions.deleteRolePermissionFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    ),

    // On Bulk Delete
    on(RolePermissionsActions.bulkDeleteRolePermissions, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(
        RolePermissionsActions.bulkDeleteRolePermissionsSuccess,
        (state, { ids }) => ({
            ...state,
            isLoading: false,
            rolePermissions: state.rolePermissions.filter(
                (rp) => !ids.includes(rp.id)
            ),
        })
    ),
    on(
        RolePermissionsActions.bulkDeleteRolePermissionsFailure,
        (state, { error }) => ({
            ...state,
            isLoading: false,
            error: error,
        })
    )
);
